import React, { useState, useEffect, useCallback } from 'react';
import { captionAPI } from '../services/api';
import useIsMobile from '../hooks/useIsMobile';

const PLATFORMS = [
  { id: 'INSTAGRAM', icon: '📸', label: 'Instagram', color: '#c13584' },
  { id: 'LINKEDIN',  icon: '💼', label: 'LinkedIn',  color: '#0077b5' },
  { id: 'TWITTER',   icon: '🐦', label: 'Twitter/X', color: '#141414' },
  { id: 'FACEBOOK',  icon: '👥', label: 'Facebook',  color: '#1877f2' },
  { id: 'YOUTUBE',   icon: '▶️', label: 'YouTube',   color: '#ff0000' },
];

const POST_TYPES = [
  { id: 'EDUCATIONAL',   icon: '📚', label: 'Educational' },
  { id: 'TUTORIAL',      icon: '🎓', label: 'Tutorial / How-To' },
  { id: 'PRODUCT_REVIEW',icon: '⭐', label: 'Product Review' },
  { id: 'MOTIVATION',    icon: '🔥', label: 'Motivation' },
  { id: 'BEHIND_SCENES', icon: '🎬', label: 'Behind the Scenes' },
  { id: 'ANNOUNCEMENT',  icon: '📣', label: 'Announcement' },
  { id: 'COLLABORATION', icon: '🤝', label: 'Collaboration' },
  { id: 'PERSONAL',      icon: '💛', label: 'Personal / Story' },
  { id: 'PROMOTIONAL',   icon: '💰', label: 'Promotional' },
];

const TONES = [
  { id: 'CASUAL',        label: '😊 Casual',        desc: 'Friendly & relatable' },
  { id: 'PROFESSIONAL',  label: '👔 Professional',   desc: 'Authoritative & polished' },
  { id: 'HUMOROUS',      label: '😂 Humorous',       desc: 'Fun & entertaining' },
  { id: 'INSPIRATIONAL', label: '✨ Inspirational',  desc: 'Uplifting & motivating' },
  { id: 'EDUCATIONAL',   label: '🎓 Educational',    desc: 'Informative & factual' },
];

const NICHES = ['Tech', 'Fitness', 'Finance', 'Beauty', 'Food', 'Travel', 'Fashion', 'Lifestyle', 'Business', 'Parenting'];

const CTA_SUGGESTIONS = [
  'Save this post!', 'Tag someone who needs this!', 'Comment your thoughts below 👇',
  'DM me for more info', 'Link in bio 👆', 'Share with a friend!',
  'Follow for more tips', 'Drop a ❤️ if you agree', 'What do you think? Let me know 👇',
];

export default function CaptionPage() {
  const isMobile = useIsMobile();
  const [history, setHistory]         = useState([]);
  const [platform, setPlatform]       = useState('INSTAGRAM');
  const [postType, setPostType]       = useState('EDUCATIONAL');
  const [tone, setTone]               = useState('CASUAL');
  const [niche, setNiche]             = useState('Tech');
  const [topic, setTopic]             = useState('');
  const [cta, setCta]                 = useState('');
  const [result, setResult]           = useState(null);
  const [activeTab, setActiveTab]     = useState('caption'); // caption | alt | hashtags
  const [loading, setLoading]         = useState(false);
  const [copiedKey, setCopiedKey]     = useState(null);
  const [view, setView]               = useState('generate'); // generate | history
  const [charCount, setCharCount]     = useState(0);
  const [error, setError]             = useState('');

  const load = useCallback(async () => {
    try { const r = await captionAPI.getAll(); setHistory(r.data); } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const platformInfo = PLATFORMS.find(p => p.id === platform) || PLATFORMS[0];

  const handleGenerate = async () => {
    if (!topic.trim()) return setError('Please describe your post topic.');
    setError('');
    setLoading(true);
    try {
      const res = await captionAPI.generate({ platform, postType, tone, niche, topic, cta });
      setResult(res.data);
      setCharCount(res.data.caption?.length || 0);
      setActiveTab('caption');
      await load();
    } catch (e) {
      // 429 from backend (AI_DAILY_LIMIT) carries the friendly text in `message`.
      const d = e.response?.data;
      setError(d?.message || d?.error || 'Generation failed.');
    }
    setLoading(false);
  };

  const copy = async (text, key) => {
    await navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2500);
  };

  const copyAll = async () => {
    if (!result) return;
    const text = `${result.caption}\n\n.\n.\n.\n${result.hashtags}`;
    await copy(text, 'all');
  };

  const handleDelete = async (id) => {
    try { await captionAPI.delete(id); await load(); } catch {}
  };

  const platColor = platformInfo.color;

  return (
    <div style={{ padding: '2rem', maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem' }}>
        <div>
          <h1 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '1.8rem', margin: 0, color: 'var(--ink)' }}>
            ✨ Caption Generator
          </h1>
          <p style={{ color: '#64748b', marginTop: '0.3rem', fontSize: '0.9rem' }}>
            Generate platform-optimised captions + hashtags for any post — tailored to your niche and tone
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {['generate','history'].map(v => (
            <button key={v} onClick={() => setView(v)}
              style={{ padding: '0.55rem 1.1rem', borderRadius: 8, border: '1px solid #e2e8f0',
                       background: view === v ? 'var(--ink)' : 'white',
                       color: view === v ? 'white' : '#64748b',
                       fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer', textTransform: 'capitalize' }}>
              {v === 'generate' ? '✨ Generate' : `📚 History (${history.length})`}
            </button>
          ))}
        </div>
      </div>

      {view === 'generate' ? (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '420px 1fr', gap: '1.5rem' }}>
          {/* LEFT — Input Panel */}
          <div>
            {/* Platform Picker */}
            <div style={{ background: 'white', borderRadius: 14, border: '1px solid #e2e8f0', padding: '1.25rem', marginBottom: '1rem' }}>
              <div style={sectionLabel}>Platform</div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {PLATFORMS.map(p => (
                  <button key={p.id} onClick={() => setPlatform(p.id)}
                    style={{ padding: '0.5rem 0.8rem', borderRadius: 9, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem',
                             border: `2px solid ${platform === p.id ? p.color : '#e2e8f0'}`,
                             background: platform === p.id ? p.color + '12' : 'white',
                             fontWeight: 700, fontSize: '0.78rem',
                             color: platform === p.id ? p.color : '#374151' }}>
                    {p.icon} {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Post Type */}
            <div style={{ background: 'white', borderRadius: 14, border: '1px solid #e2e8f0', padding: '1.25rem', marginBottom: '1rem' }}>
              <div style={sectionLabel}>Post Type</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.45rem' }}>
                {POST_TYPES.map(pt => (
                  <button key={pt.id} onClick={() => setPostType(pt.id)}
                    style={{ padding: '0.55rem 0.4rem', borderRadius: 9, cursor: 'pointer', textAlign: 'center',
                             border: `2px solid ${postType === pt.id ? platColor : '#e2e8f0'}`,
                             background: postType === pt.id ? platColor + '10' : 'white',
                             fontWeight: 600, fontSize: '0.72rem',
                             color: postType === pt.id ? platColor : '#374151' }}>
                    <div style={{ fontSize: '1.1rem', marginBottom: '0.15rem' }}>{pt.icon}</div>
                    {pt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tone */}
            <div style={{ background: 'white', borderRadius: 14, border: '1px solid #e2e8f0', padding: '1.25rem', marginBottom: '1rem' }}>
              <div style={sectionLabel}>Tone of Voice</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {TONES.map(t => (
                  <button key={t.id} onClick={() => setTone(t.id)}
                    style={{ padding: '0.6rem 0.85rem', borderRadius: 9, cursor: 'pointer', textAlign: 'left', display: 'flex', gap: '0.75rem', alignItems: 'center',
                             border: `2px solid ${tone === t.id ? platColor : '#e2e8f0'}`,
                             background: tone === t.id ? platColor + '10' : 'white' }}>
                    <span style={{ fontWeight: 700, fontSize: '0.82rem', color: tone === t.id ? platColor : '#1e293b' }}>{t.label}</span>
                    <span style={{ fontSize: '0.72rem', color: '#94a3b8', marginLeft: 'auto' }}>{t.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Topic + Niche + CTA */}
            <div style={{ background: 'white', borderRadius: 14, border: '1px solid #e2e8f0', padding: '1.25rem', marginBottom: '1rem' }}>
              <div style={sectionLabel}>Post Details</div>

              <div style={{ marginBottom: '0.85rem' }}>
                <label style={fldLbl}>What is this post about? *</label>
                <textarea value={topic} onChange={e => setTopic(e.target.value)}
                  placeholder="e.g. I tested the new OnePlus 13 for 2 weeks and here are my honest thoughts..."
                  rows={3} style={{ width: '100%', padding: '0.6rem 0.8rem', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.83rem', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.85rem' }}>
                <div>
                  <label style={fldLbl}>Your Niche</label>
                  <select value={niche} onChange={e => setNiche(e.target.value)}
                    style={{ width: '100%', padding: '0.55rem 0.7rem', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.83rem', outline: 'none', background: 'white', boxSizing: 'border-box' }}>
                    {NICHES.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div>
                  <label style={fldLbl}>Call to Action</label>
                  <input value={cta} onChange={e => setCta(e.target.value)} list="cta-list"
                    placeholder="e.g. Save this post!"
                    style={{ width: '100%', padding: '0.55rem 0.7rem', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.83rem', outline: 'none', boxSizing: 'border-box' }} />
                  <datalist id="cta-list">
                    {CTA_SUGGESTIONS.map(c => <option key={c} value={c} />)}
                  </datalist>
                </div>
              </div>
            </div>

            {error && (
              <div style={{ background: '#fee2e2', borderRadius: 8, padding: '0.6rem 0.9rem', fontSize: '0.82rem', color: '#991b1b', marginBottom: '0.75rem' }}>
                {error}
              </div>
            )}

            <button onClick={handleGenerate} disabled={loading}
              style={{ width: '100%', padding: '0.85rem', borderRadius: 11, border: 'none',
                       background: loading ? '#94a3b8' : `linear-gradient(135deg, ${platColor}, ${platColor}cc)`,
                       color: 'white', fontWeight: 800, fontSize: '0.92rem',
                       cursor: loading ? 'not-allowed' : 'pointer', letterSpacing: '-0.01em' }}>
              {loading ? '⏳ Generating…' : `✨ Generate ${platformInfo.label} Caption`}
            </button>
          </div>

          {/* RIGHT — Output Panel */}
          <div>
            {result ? (
              <div style={{ background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden', position: 'sticky', top: '1rem' }}>
                {/* Output Header */}
                <div style={{ background: `linear-gradient(135deg, ${platColor}, ${platColor}cc)`, padding: '1rem 1.5rem',
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.1rem' }}>
                      Generated Caption
                    </div>
                    <div style={{ color: 'white', fontWeight: 700, fontSize: '0.88rem' }}>
                      {platformInfo.icon} {platformInfo.label} · {POST_TYPES.find(p => p.id === result.postType)?.label} · {TONES.find(t => t.id === result.tone)?.label?.split(' ')[1]}
                    </div>
                  </div>
                  <button onClick={copyAll}
                    style={{ padding: '0.45rem 0.9rem', borderRadius: 8, border: 'none', cursor: 'pointer',
                             background: copiedKey === 'all' ? '#10b981' : 'rgba(255,255,255,0.2)',
                             color: 'white', fontWeight: 600, fontSize: '0.78rem' }}>
                    {copiedKey === 'all' ? '✅ Copied!' : '📋 Copy All'}
                  </button>
                </div>

                {/* Tabs */}
                <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0' }}>
                  {[
                    { key: 'caption',  label: '📝 Caption' },
                    { key: 'alt',      label: '🔄 Alt Version' },
                    { key: 'hashtags', label: '# Hashtags' },
                  ].map(tab => (
                    <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                      style={{ flex: 1, padding: '0.75rem', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem',
                               background: 'transparent',
                               color: activeTab === tab.key ? platColor : '#64748b',
                               borderBottom: `2px solid ${activeTab === tab.key ? platColor : 'transparent'}` }}>
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Content */}
                <div style={{ padding: '1.5rem', minHeight: 300, maxHeight: 480, overflowY: 'auto' }}>
                  {activeTab === 'caption' && (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{charCount} characters</span>
                        <button onClick={() => copy(result.caption, 'cap')}
                          style={{ padding: '0.3rem 0.75rem', borderRadius: 7, border: 'none', cursor: 'pointer',
                                   background: copiedKey === 'cap' ? '#d1fae5' : '#f1f5f9',
                                   color: copiedKey === 'cap' ? '#065f46' : '#374151',
                                   fontWeight: 600, fontSize: '0.73rem' }}>
                          {copiedKey === 'cap' ? '✅ Copied!' : '📋 Copy Caption'}
                        </button>
                      </div>
                      <pre style={{ fontFamily: 'inherit', whiteSpace: 'pre-wrap', fontSize: '0.88rem', color: '#1e293b', lineHeight: 1.7, margin: 0 }}>
                        {result.caption}
                      </pre>
                    </>
                  )}
                  {activeTab === 'alt' && (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>Alternative version with different hook</span>
                        <button onClick={() => copy(result.altCaption, 'alt')}
                          style={{ padding: '0.3rem 0.75rem', borderRadius: 7, border: 'none', cursor: 'pointer',
                                   background: copiedKey === 'alt' ? '#d1fae5' : '#f1f5f9',
                                   color: copiedKey === 'alt' ? '#065f46' : '#374151',
                                   fontWeight: 600, fontSize: '0.73rem' }}>
                          {copiedKey === 'alt' ? '✅ Copied!' : '📋 Copy Alt'}
                        </button>
                      </div>
                      <pre style={{ fontFamily: 'inherit', whiteSpace: 'pre-wrap', fontSize: '0.88rem', color: '#1e293b', lineHeight: 1.7, margin: 0 }}>
                        {result.altCaption}
                      </pre>
                    </>
                  )}
                  {activeTab === 'hashtags' && (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                        <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                          {result.hashtags?.split(' ').length || 0} hashtags · {niche} niche · {platformInfo.label}
                        </span>
                        <button onClick={() => copy(result.hashtags, 'hash')}
                          style={{ padding: '0.3rem 0.75rem', borderRadius: 7, border: 'none', cursor: 'pointer',
                                   background: copiedKey === 'hash' ? '#d1fae5' : '#f1f5f9',
                                   color: copiedKey === 'hash' ? '#065f46' : '#374151',
                                   fontWeight: 600, fontSize: '0.73rem' }}>
                          {copiedKey === 'hash' ? '✅ Copied!' : '📋 Copy Hashtags'}
                        </button>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                        {result.hashtags?.split(' ').map((tag, i) => (
                          <span key={i} onClick={() => copy(tag, `tag-${i}`)}
                            style={{ padding: '0.3rem 0.7rem', borderRadius: 20, background: platColor + '12',
                                     color: platColor, fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer',
                                     border: `1px solid ${platColor}25`,
                                     opacity: copiedKey === `tag-${i}` ? 0.6 : 1 }}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Quick tip */}
                <div style={{ padding: '0.85rem 1.5rem', borderTop: '1px solid #f1f5f9', background: '#f8fafc',
                              fontSize: '0.73rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>💡</span>
                  <span>
                    {platform === 'INSTAGRAM' ? 'Pro tip: Post hashtags in the first comment to keep your caption clean.' :
                     platform === 'LINKEDIN'  ? 'Pro tip: LinkedIn posts with 3-5 hashtags get 30% more reach than heavy hashtag posts.' :
                     platform === 'TWITTER'   ? 'Pro tip: 1-2 hashtags on Twitter outperform posts with many hashtags.' :
                     'Pro tip: Save your best captions and reuse them for similar content next month.'}
                  </span>
                </div>
              </div>
            ) : (
              <div style={{ background: 'white', borderRadius: 16, border: '2px dashed #e2e8f0',
                            minHeight: 400, display: 'flex', flexDirection: 'column',
                            alignItems: 'center', justifyContent: 'center', color: '#94a3b8', textAlign: 'center', padding: '2rem' }}>
                <div style={{ fontSize: '3.5rem', marginBottom: '1rem' }}>✨</div>
                <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '0.4rem', color: '#64748b' }}>
                  Your caption will appear here
                </div>
                <div style={{ fontSize: '0.82rem', lineHeight: 1.5, maxWidth: 280 }}>
                  Choose a platform, post type, tone, and describe your content — then click Generate
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* HISTORY VIEW */
        <div>
          {history.length === 0 ? (
            <div style={{ background: 'white', borderRadius: 16, border: '2px dashed #e2e8f0',
                          padding: '4rem 2rem', textAlign: 'center', color: '#94a3b8' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📚</div>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: '#64748b' }}>No captions generated yet</div>
              <div style={{ fontSize: '0.82rem', marginTop: '0.4rem' }}>Go to Generate and create your first caption</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '0.8rem' }}>
              {history.map(j => {
                const pl = PLATFORMS.find(p => p.id === j.platform) || PLATFORMS[0];
                const pt = POST_TYPES.find(p => p.id === j.postType);
                return (
                  <div key={j.id} style={{ background: 'white', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <div style={{ padding: '1rem 1.25rem', display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                      <div style={{ width: 42, height: 42, borderRadius: 10, flexShrink: 0, background: pl.color + '15',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>
                        {pl.icon}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.3rem' }}>
                          <span style={{ fontWeight: 700, fontSize: '0.85rem', color: pl.color }}>{pl.label}</span>
                          {pt && <span style={{ fontSize: '0.72rem', color: '#64748b' }}>{pt.icon} {pt.label}</span>}
                          <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{j.niche} · {j.tone}</span>
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {j.topic}
                        </div>
                        <div style={{ fontSize: '0.73rem', color: '#94a3b8', marginTop: '0.2rem' }}>
                          {new Date(j.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                        <button onClick={() => copy(j.caption, `hist-${j.id}`)}
                          style={{ padding: '0.38rem 0.7rem', borderRadius: 8, border: '1px solid #e2e8f0',
                                   background: copiedKey === `hist-${j.id}` ? '#d1fae5' : 'white',
                                   color: copiedKey === `hist-${j.id}` ? '#065f46' : '#374151',
                                   fontWeight: 600, fontSize: '0.73rem', cursor: 'pointer' }}>
                          {copiedKey === `hist-${j.id}` ? '✅' : '📋'}
                        </button>
                        <button onClick={() => handleDelete(j.id)}
                          style={{ padding: '0.38rem 0.65rem', borderRadius: 8, border: '1px solid #fee2e2',
                                   background: '#fff5f5', color: '#ef4444', fontWeight: 600, fontSize: '0.73rem', cursor: 'pointer' }}>
                          🗑
                        </button>
                      </div>
                    </div>
                    <div style={{ padding: '0 1.25rem 0.85rem' }}>
                      <pre style={{ margin: 0, fontFamily: 'inherit', whiteSpace: 'pre-wrap', fontSize: '0.8rem',
                                    color: '#64748b', lineHeight: 1.5, maxHeight: 80, overflow: 'hidden',
                                    maskImage: 'linear-gradient(to bottom, black 60%, transparent)' }}>
                        {j.caption}
                      </pre>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const sectionLabel = { fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.75rem' };
const fldLbl = { fontSize: '0.72rem', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.03em' };
